import {
  type CSSProperties,
  type ForwardedRef,
  type ReactNode,
  type UIEventHandler,
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { useForwardedRef, useResizeObserver } from '../../hooks'
import type { RowRange, VirtualScrollProps } from './types'

type CreateStylePropsValue = {
  position: string
  inset: number
  transform: string
  height: string
}

function createStyleProps(row: number, height: number, gap: number): CreateStylePropsValue {
  return {
    position: 'absolute',
    inset: 0,
    transform: `translateY(${row * (height + gap)}px)`,
    height: `${height}px`,
  }
}

const ScrollingStates = {
  Up: 'Up',
  Down: 'Down',
  None: null,
} as const

type ScrollingStates = (typeof ScrollingStates)[keyof typeof ScrollingStates]

// eslint-disable-next-line import/exports-last
export function VirtualScrollSSRFriendly(props: VirtualScrollProps) {
  const [mounted, setMounted] = useState(false)
  useEffect(() => {
    setMounted(true)
    return () => {
      setMounted(false)
    }
  }, [])
  // do not render the if we're in SSR mode as useLayoutEffect is unavailable
  if (!mounted) return null

  return <VirtualScroll {...props} />
}

// eslint-disable-next-line import/exports-last
export function getScrollPosOnHeightChange(rowHeight: number, gap: number, row: number, scrollPos: number) {
  const totalRowHeight = rowHeight + gap
  const rowPos = row * totalRowHeight
  const positionInARow = scrollPos % totalRowHeight
  const newScrollPos = rowPos + positionInARow
  return newScrollPos
}

function propsHaveChanged(prevProps: Record<string, unknown> | undefined, props: Record<string, unknown> | undefined) {
  if (prevProps === props) return false
  if (!prevProps && props) return true
  if (!props && prevProps) return true
  return Object.keys(props!).some((key) => props![key] !== prevProps![key])
}
/**
 * Stabilizes the props of the first row to avoid unnecessary re-renders
 * Check if any of the props have changed and if so, update the state
 * @param props - The props to stabilize
 * @returns The stabilized props
 */
function useStabilizedProps(props: Record<string, unknown> | undefined) {
  const [prevProps, setPrevProps] = useState(props)
  useEffect(() => {
    if (propsHaveChanged(prevProps, props)) {
      setPrevProps(props)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props])
  return prevProps
}

const VirtualScroll = forwardRef(function VirtualScroll(
  {
    className,
    style,
    children: Row,
    overscanRows: overscanRows_,
    maxRows: maxRows_,
    rowHeight: initialRowHeight = 0,
    rows,
    onScroll: onScrollExternal,
    initialRow,
    gap = 0,
    reactiveProps: reactiveProps_,
  }: VirtualScrollProps,
  fwdRef: ForwardedRef<HTMLDivElement | null>
) {
  const stabilizedProps = useStabilizedProps(reactiveProps_)
  const [containerRef, ref] = useForwardedRef<HTMLDivElement | null>(fwdRef)
  const overscanRows = overscanRows_ || rows // do not allow for a zero
  const cacheRef = useRef<Map<number, CreateStylePropsValue>>(new Map())
  const [rowHeight, setRowHeight] = useState(initialRowHeight)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const scrollPos = useRef<number>((initialRow ?? 0) * rowHeight)
  const [row, setRow] = useState<number>(0)
  const rowRef = useRef<number>(0)
  const scrollingState = useRef<ScrollingStates>(ScrollingStates.None)
  const maxRows = maxRows_ ?? row + (rows + overscanRows) * 2 + 1
  const containerHeight = rows * (rowHeight + gap)
  const totalHeight = (rowHeight + gap) * maxRows

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { width } = useResizeObserver({ ref: containerRef })
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useLayoutEffect(() => {
    const firstItem = containerRef.current?.firstElementChild?.firstElementChild
    if (!firstItem) return
    setRowHeight(firstItem.clientHeight)
  }, [containerRef, width, stabilizedProps])

  const rowHeightInPx = `${rowHeight}px`

  function getStyle(index: number): CSSProperties {
    if (!cacheRef.current.has(index) || cacheRef.current.get(index)?.height !== rowHeightInPx) {
      cacheRef.current.set(index, createStyleProps(index, rowHeight, gap))
    }
    return cacheRef.current.get(index)! as CSSProperties
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const getRange = useCallback(
    (row: number): RowRange => {
      const startIndex = Math.max(0, row - overscanRows)
      const endIndex = Math.min(
        maxRows - 1, // don't render past the end of the list
        row + rows + overscanRows
      )
      return [startIndex, endIndex, row]
    },
    [maxRows, overscanRows, rows]
  )

  const items: ReactNode[] = []
  const [start, end] = getRange(row)
  for (let i = start; i <= end; i++) {
    items.push(<Row index={i} style={getStyle(i)} key={i} />)
  }

  const onScrollDebounced = useCallback(
    (range: RowRange) => {
      if (timeoutRef.current !== null) clearTimeout(timeoutRef.current)
      timeoutRef.current = setTimeout(() => {
        onScrollExternal?.(range)
        timeoutRef.current = null
      }, 50)
    },
    [onScrollExternal]
  )

  useEffect(() => {
    rowRef.current = row
  }, [row])

  useLayoutEffect(() => {
    const currentScrollPos = containerRef.current?.scrollTop ?? 0
    const scrollPos = getScrollPosOnHeightChange(rowHeight, gap, row, currentScrollPos)
    containerRef.current?.scrollTo?.({ left: 0, top: scrollPos, behavior: 'instant' })
    onScrollExternal?.(getRange(row))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowHeight, gap, maxRows])

  const onScroll: UIEventHandler<HTMLDivElement> = useCallback(
    (ev) => {
      const prevScrollPos = scrollPos.current
      scrollPos.current = ev.currentTarget.scrollTop
      const row = Math.floor(scrollPos.current / (rowHeight + gap))
      scrollingState.current = prevScrollPos < scrollPos.current ? ScrollingStates.Down : ScrollingStates.Up
      onScrollDebounced(getRange(row))
      setRow(row)
    },
    [getRange, onScrollDebounced, rowHeight, gap]
  )

  useEffect(() => {
    if (!initialRow) return
    containerRef.current?.scrollTo?.({ left: 0, top: initialRow * (rowHeight + gap), behavior: 'instant' })
    onScrollExternal?.(getRange(initialRow))
  }, [getRange, initialRow, onScrollExternal, rowHeight, gap, containerRef])

  useEffect(() => {
    return () => {
      if (timeoutRef?.current) clearTimeout(timeoutRef.current)
    }
  }, [])

  return (
    <div
      className={className}
      style={{
        // allow to override the height
        height: containerHeight,
        ...style,
        overflowY: 'auto',
        position: 'relative',
        scrollBehavior: 'smooth',
        willChange: 'contents',
        maxHeight: containerHeight,
      }}
      ref={ref}
      onScroll={onScroll}
    >
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: totalHeight }}>
        <Row {...stabilizedProps} index={-1} key={-1} style={{ opacity: '0', pointerEvents: 'none' }}></Row>
        {items}
      </div>
    </div>
  )
})

export default memo(VirtualScroll)
